import { HOW_TO_CATEGORIES, howToCategoryPath } from './howToCategories';
import { articles as mockArticles } from '../mocks/articles';
import { resources as mockResources } from '../mocks/resources';

/**
 * Shared internal-link builder used by BOTH the runtime shell
 * (src/components/feature/RouteRelatedContent.tsx) and the build-time
 * prerenderer (scripts/prerender.mjs), so every page ships the same crawlable
 * links whether it is server-rendered or emitted from the deterministic block.
 *
 * Targets:
 *   • service pages  → related how-to videos + related insights
 *   • article pages  → 2–3 related service pages + 2 related insights
 *   • resource pages → 2–3 related service pages + 2 related insights
 */

export interface RelatedLink {
  to: string;
  label: string;
  desc: string;
}

/** Canonical label/description for every service page we ever link to. */
export const SERVICE_LABELS: Record<string, RelatedLink> = {
  '/services': { to: '/services', label: 'All IT Services', desc: 'ERP, cloud, AI, staffing, managed IT and more.' },
  '/services/erp': { to: '/services/erp', label: 'ERP Services', desc: 'Implementation, upgrades and managed ERP support.' },
  '/services/erp/implementation': { to: '/services/erp/implementation', label: 'ERP Implementation', desc: 'On-time, on-budget ERP deployments.' },
  '/services/erp/managed-services': { to: '/services/erp/managed-services', label: 'ERP Managed Services', desc: 'Ongoing AMS support and administration.' },
  '/services/cloud': { to: '/services/cloud', label: 'Cloud Services', desc: 'Migration and managed cloud on AWS, Azure and GCP.' },
  '/services/cloud/migration': { to: '/services/cloud/migration', label: 'Cloud Migration', desc: 'Move to the cloud with minimal downtime.' },
  '/services/app/cloud-services': { to: '/services/app/cloud-services', label: 'Cloud-Native Apps', desc: 'Microservices, containers and serverless.' },
  '/services/ai': { to: '/services/ai', label: 'AI & Automation', desc: 'AI strategy, automation and analytics.' },
  '/services/ai/machine-learning': { to: '/services/ai/machine-learning', label: 'Machine Learning', desc: 'Predictive models and intelligent decisions.' },
  '/services/ai/business-intelligence': { to: '/services/ai/business-intelligence', label: 'Business Intelligence', desc: 'Power BI, data warehousing and analytics.' },
  '/services/managed': { to: '/services/managed', label: 'Managed IT Services', desc: 'Help desk, monitoring and co-managed IT.' },
  '/services/managed/infrastructure': { to: '/services/managed/infrastructure', label: 'Managed Infrastructure', desc: 'Network, server and storage management.' },
  '/services/managed/security': { to: '/services/managed/security', label: 'Managed Cybersecurity', desc: 'Threat detection, HIPAA, SOC 2 and CMMC.' },
  '/services/managed-compliance': { to: '/services/managed-compliance', label: 'Managed Compliance', desc: 'HIPAA, SOC 2, CMMC and NIST programs.' },
  '/services/tech-assessment': { to: '/services/tech-assessment', label: 'Technology Assessment', desc: 'IT audit, gap analysis and a roadmap.' },
  '/services/service-desk': { to: '/services/service-desk', label: 'IT Service Desk', desc: 'L1, L2 and field support with SLAs.' },
  '/services/staffing': { to: '/services/staffing', label: 'IT Staffing', desc: 'Contract, managed-team and direct-hire talent.' },
  '/services/staffing/contract': { to: '/services/staffing/contract', label: 'Contract Staffing', desc: 'On-demand IT talent across DFW.' },
  '/services/staffing/direct-hire': { to: '/services/staffing/direct-hire', label: 'Direct Hire', desc: 'Permanent placement with a 90-day guarantee.' },
  '/services/healthcare-it': { to: '/services/healthcare-it', label: 'Healthcare IT', desc: 'EHR, interoperability and HIPAA.' },
  '/services/healthcare-it/ehr': { to: '/services/healthcare-it/ehr', label: 'EHR Implementation', desc: 'Epic, Cerner and health-record systems.' },
  '/services/strategy-advisory': { to: '/services/strategy-advisory', label: 'IT Strategy Advisory', desc: 'Roadmaps and IT governance.' },
  '/services/digital-transformation': { to: '/services/digital-transformation', label: 'Digital Transformation', desc: 'Modernization, cloud and automation.' },
};

/** Curated related insights per service area (slug → /insights/<slug>). */
export const SERVICE_INSIGHTS: Record<string, string[]> = {
  '/services/erp': ['erp-implementation-best-practices', 'sap-s4hana-migration-guide', 'manufacturing-erp-cloud-migration'],
  '/services/erp/implementation': ['erp-implementation-best-practices', 'manufacturing-erp-cloud-migration'],
  '/services/erp/managed-services': ['erp-implementation-best-practices', 'manufacturing-erp-cloud-migration'],
  '/services/cloud': ['cloud-migration-aws-azure-google', 'kubernetes-enterprise-adoption', 'financial-services-cloud-security-compliance'],
  '/services/cloud/migration': ['cloud-migration-aws-azure-google', 'manufacturing-erp-cloud-migration'],
  '/services/app/cloud-services': ['cloud-migration-aws-azure-google', 'kubernetes-enterprise-adoption'],
  '/services/ai': ['ai-machine-learning-business-intelligence', 'generative-ai-enterprise-use-cases', 'ai-governance-responsible-enterprise-ai'],
  '/services/ai/machine-learning': ['ai-machine-learning-business-intelligence', 'generative-ai-enterprise-use-cases'],
  '/services/ai/business-intelligence': ['ai-machine-learning-business-intelligence', 'retail-ai-personalization-loyalty'],
  '/services/managed': ['roi-of-it-managed-services', 'managed-services-buyer-guide-2024', 'cybersecurity-threats-2024'],
  '/services/managed/infrastructure': ['roi-of-it-managed-services', 'managed-services-buyer-guide-2024'],
  '/services/managed/security': ['cybersecurity-threats-2024', 'zero-trust-implementation-guide', 'managed-services-buyer-guide-2024'],
  '/services/managed-compliance': ['hipaa-compliance-guide-2024', 'zero-trust-implementation-guide', 'cybersecurity-threats-2024'],
  '/services/tech-assessment': ['it-strategy-business-alignment-framework', 'zero-trust-implementation-guide'],
  '/services/service-desk': ['roi-of-it-managed-services', 'managed-services-buyer-guide-2024'],
  '/services/staffing': ['it-staffing-vs-direct-hire', 'building-high-performing-it-teams-remote', 'it-talent-shortage-strategies-2024'],
  '/services/staffing/contract': ['it-staffing-vs-direct-hire', 'it-talent-shortage-strategies-2024'],
  '/services/staffing/direct-hire': ['it-staffing-vs-direct-hire', 'it-talent-shortage-strategies-2024'],
  '/services/healthcare-it': ['future-of-healthcare-it-trends-2024', 'hipaa-compliance-guide-2024'],
  '/services/healthcare-it/ehr': ['future-of-healthcare-it-trends-2024', 'hipaa-compliance-guide-2024'],
  '/services/strategy-advisory': ['it-strategy-business-alignment-framework', 'digital-transformation-where-to-start'],
  '/services/digital-transformation': ['digital-transformation-where-to-start', 'it-strategy-business-alignment-framework'],
};

/** Article/resource category+topic → 2–3 relevant service pages. */
export const CATEGORY_SERVICES: Record<string, string[]> = {
  'Cloud & Infrastructure': ['/services/cloud', '/services/cloud/migration', '/services/app/cloud-services'],
  Cybersecurity: ['/services/managed/security', '/services/managed-compliance', '/services/tech-assessment'],
  'IT Staffing': ['/services/staffing', '/services/staffing/contract', '/services/staffing/direct-hire'],
  'ERP & Enterprise': ['/services/erp', '/services/erp/implementation', '/services/erp/managed-services'],
  'Analytics & AI': ['/services/ai', '/services/ai/machine-learning', '/services/ai/business-intelligence'],
  'Managed Services': ['/services/managed', '/services/service-desk', '/services/managed/infrastructure'],
  'Healthcare IT': ['/services/healthcare-it', '/services/healthcare-it/ehr', '/services/managed-compliance'],
  Strategy: ['/services/strategy-advisory', '/services/digital-transformation', '/services/tech-assessment'],
  'Government & Compliance': ['/services/managed-compliance', '/services/tech-assessment', '/services/managed/security'],
  'Business Analysis': ['/services/staffing', '/services/strategy-advisory', '/services/managed-compliance'],
  'AI & Innovation': ['/services/ai', '/services/ai/machine-learning', '/services/ai/business-intelligence'],
  'Retail & Commerce': ['/services/erp', '/services/ai/business-intelligence', '/services/managed'],
  'Manufacturing & Operations': ['/services/erp', '/services/erp/implementation', '/services/managed'],
  'Financial Services': ['/services/managed/security', '/services/managed-compliance', '/services/cloud'],
  'Transportation & Logistics': ['/services/cloud', '/services/managed', '/services/erp'],
  'Nonprofit & Social Impact': ['/services/managed', '/services/cloud', '/services/staffing'],
  'Legal & Professional Services': ['/services/managed/security', '/services/managed', '/services/managed-compliance'],
  'Energy & Utilities': ['/services/managed/security', '/services/erp', '/services/cloud'],
  'Hospitality & Real Estate': ['/services/managed', '/services/cloud', '/services/app/development-services'],
  'Industry Insights': ['/services/erp', '/services/managed', '/services/ai'],
  'Best Practices': ['/services/erp', '/services/erp/implementation', '/services/managed'],
};

export const DEFAULT_SERVICES = ['/services/erp', '/services/managed', '/services/ai'];

export function toServiceLinks(paths: string[]): RelatedLink[] {
  return paths.map((p) => SERVICE_LABELS[p]).filter(Boolean);
}

/** Longest matching service prefix for a pathname. */
export function matchServiceKey(pathname: string, map: Record<string, unknown>): string | undefined {
  return Object.keys(map)
    .filter((k) => pathname === k || pathname.startsWith(`${k}/`))
    .sort((a, b) => b.length - a.length)[0];
}

/** Service page → related how-to videos + related insights. */
export function getServicePageRelated(pathname: string): { videos: RelatedLink[]; insights: RelatedLink[] } {
  const catKey = matchServiceKey(pathname, Object.fromEntries(HOW_TO_CATEGORIES.map((c) => [c.servicePath, c])));
  const category = catKey ? HOW_TO_CATEGORIES.find((c) => c.servicePath === catKey) : undefined;

  const videos: RelatedLink[] = [];
  if (category) {
    videos.push({ to: howToCategoryPath(category.slug), label: `${category.label} how-to videos`, desc: category.intro });
    (category.subCategories || []).slice(0, 2).forEach((sub) => {
      videos.push({
        to: howToCategoryPath(category.slug, sub.slug),
        label: `${sub.label} how-to videos`,
        desc: sub.intro,
      });
    });
  }
  videos.push({ to: '/videos/how-tos', label: 'All how-to videos', desc: 'Every step-by-step IT guide, with written steps.' });

  const insightKey = matchServiceKey(pathname, SERVICE_INSIGHTS) || '/services';
  const slugs = SERVICE_INSIGHTS[insightKey] || [];
  const insights = slugs
    .map((slug) => {
      const a = mockArticles.find((x) => x.slug === slug);
      return a ? { to: `/insights/${a.slug}`, label: a.title, desc: a.excerpt } : null;
    })
    .filter(Boolean) as RelatedLink[];

  return { videos: videos.slice(0, 3), insights: insights.slice(0, 3) };
}

/** Two related insights for a detail page, preferring the same category. */
export function relatedInsights(category?: string, excludeSlug?: string): RelatedLink[] {
  const pool = mockArticles.filter((a) => a.slug !== excludeSlug);
  const same = category ? pool.filter((a) => a.category === category) : [];
  const picks = [...same, ...pool.filter((a) => !same.includes(a))].slice(0, 2);
  return picks.map((a) => ({ to: `/insights/${a.slug}`, label: a.title, desc: a.excerpt }));
}

/**
 * Detail page (insight or resource) → 2–3 related service pages + 2 related
 * insights. Works from a category OR a resource topic.
 */
export function getDetailRelated(opts: {
  slug?: string;
  category?: string;
  topic?: string;
}): { services: RelatedLink[]; insights: RelatedLink[] } {
  const key = opts.category || opts.topic || '';
  const paths = CATEGORY_SERVICES[key] || DEFAULT_SERVICES;
  return {
    services: toServiceLinks(paths).slice(0, 3),
    insights: relatedInsights(opts.category || opts.topic, opts.slug),
  };
}

/** Resource page (resolved from the bundled mock list). */
export function getResourceRelated(slug: string): { services: RelatedLink[]; insights: RelatedLink[] } {
  const r = mockResources.find((x) => x.slug === slug);
  return getDetailRelated({ slug, topic: r?.topic });
}